import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import breakpoints from "assets/theme/base/breakpoints";
import Header from "layouts/settings/components/Header";

import Plivo from "layouts/settings/components/Plivo";
import PaymentGateway from "layouts/settings/components/PaymentGateway";
import Slack from "layouts/settings/components/Slack";
import Discord from "layouts/settings/components/Discord";
import FlowType from "layouts/settings/components/FlowType";
import Integrations from "./components/Integrations";
import Extension from "./components/Extension";
import LPR from "./components/LPR";
import VIP from "./components/VIP";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomTooltip from "components/Common/CustomTooltip";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <MDBox sx={{ p: 3 }}>{children}</MDBox>}
    </div>
  );
}

const tabInfo = {
  plivoNumber: "Select an SMS-enabled number to send notifications.",
  paymentGateway: "Select the payment processor to handle transactions.",
  slack: "Configure Slack Webhook, channel, workspace & purpose.",
  discord: "Configure Discord Webhook, channel, workspace & purpose.",
  flowType: "Choose how parking commands are delivered to Parker.",
  integrations:
    "Manage system integrations that receive vehicle or entry/exit data.",
  lpr: "Use LPR to automatically detect and identify license plates.",
  vip: "Manage reserved parking spots & space settings.",
  extension: "Configure extra parking time beyond the booked period.",
};

function Overview() {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");
  const [openTooltipId, setOpenTooltipId] = useState(null);

  const userData = useSelector((state) => state.users?.meInfo);
  const roleModules = userData?.roleModules || {};

  const [tabValue, setTabValue] = useState(
    activeTab ? activeTab : roleModules.Setting_view ? "plivoNumber" : ""
  );

  useEffect(() => {
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }
    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);
    queryParams.set("tab", newValue);
    navigate(`?${queryParams.toString()}`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />

      <Header>
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
              <AppBar position="static">
                <Tabs
                  orientation={tabsOrientation}
                  value={tabValue}
                  onChange={handleSetTabValue}
                  variant="scrollable"
                  scrollButtons
                  sx={{
                    "& .MuiTab-root": {
                      paddingX: 1,
                      flexShrink: 0,
                    },
                  }}
                >
                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Plivo Number
                        <CustomTooltip
                          id="plivoNumber"
                          title={tabInfo.plivoNumber}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="plivoNumber"
                    disabled={!roleModules.Setting_plivo}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Payment Gateway
                        <CustomTooltip
                          id="paymentGateway"
                          title={tabInfo.paymentGateway}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="paymentGateway"
                    disabled={!roleModules.Setting_payment_gateway}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Slack
                        <CustomTooltip
                          id="slack"
                          title={tabInfo.slack}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="slack"
                    disabled={!roleModules.Setting_slack}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Discord
                        <CustomTooltip
                          id="discord"
                          title={tabInfo.discord}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="discord"
                    disabled={!roleModules.Setting_slack}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Flow Type
                        <CustomTooltip
                          id="flowType"
                          title={tabInfo.flowType}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="flowType"
                    disabled={!roleModules.Setting_flowType}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Integrations
                        <CustomTooltip
                          id="integrations"
                          title={tabInfo.integrations}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="integrations"
                    disabled={!roleModules.Setting_integration}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        LPR Settings
                        <CustomTooltip
                          id="lpr"
                          title={tabInfo.lpr}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="lpr"
                    disabled={!roleModules.Setting_integration}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Space Settings
                        <CustomTooltip
                          id="vip"
                          title={tabInfo.vip}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="vip"
                    disabled={!roleModules.Setting_integration}
                  />

                  <Tab
                    label={
                      <MDBox display="flex" alignItems="center">
                        Extension
                        <CustomTooltip
                          id="extension"
                          title={tabInfo.extension}
                          openTooltipId={openTooltipId}
                          setOpenTooltipId={setOpenTooltipId}
                          iconVariant="outlined"
                          iconSize={18}
                        />
                      </MDBox>
                    }
                    value="extension"
                    disabled={!roleModules.Setting_extension}
                  />
                </Tabs>
              </AppBar>

              <TabPanel value={tabValue} index={"plivoNumber"}>
                <Plivo />
              </TabPanel>

              <TabPanel value={tabValue} index="paymentGateway">
                <PaymentGateway />
              </TabPanel>

              <TabPanel value={tabValue} index="slack">
                <Slack />
              </TabPanel>

              <TabPanel value={tabValue} index="discord">
                <Discord />
              </TabPanel>

              <TabPanel value={tabValue} index="flowType">
                <FlowType />
              </TabPanel>

              <TabPanel value={tabValue} index="integrations">
                <Integrations />
              </TabPanel>

              <TabPanel value={tabValue} index="lpr">
                <LPR />
              </TabPanel>

              <TabPanel value={tabValue} index="vip">
                <VIP />
              </TabPanel>

              <TabPanel value={tabValue} index="extension">
                <Extension />
              </TabPanel>
            </Grid>
          </Grid>
        </MDBox>
      </Header>

      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
